import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ColorInput } from '@/components/ColorInput';
import { EditorTemplatesCarousel, type EditorTemplateKit } from '@/components/editor/EditorTemplatesCarousel';
import { FramePickerDialog } from '@/components/quote-carousel/FramePickerDialog';
import { QuoteTextStyleSection } from '@/components/quote-carousel/QuoteTextStyleSection';
import { DEFAULT_QUOTE_BRAND, type QuoteSlideBrand } from '@/lib/quoteCarousel/buildQuoteSlideProps';
import { resolveQuoteTextStyle } from '@/lib/quoteCarousel/quoteTextStyle';
import { cn } from '@/lib/utils';
import { EXPORT_FPS, formatFrameTime } from '@/lib/timeline/exportFrameTiming';
import { resolveEffectiveFrameSource } from '@/lib/quoteCarousel/resolveEffectiveFrameSource';
import { applyCarouselFrameSource } from '@/lib/quoteCarousel/applyCarouselFrameSource';
import type { FrameCaptureSources } from '@/lib/quoteCarousel/resolveFrameCapturePlan';
import type {
  QuoteCarousel, QuoteCarouselFormat, QuoteFrameSource, QuoteSlide, QuoteSlideStyle, QuoteTextStyle,
} from '@/lib/quoteCarousel/types';

const MAX_HANDLE_CHARS = 40;

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
  title?: string;
}

function Segmented<T extends string>({
  value, options, onChange, testId,
}: {
  /** null = no option selected. */
  value: T | null;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  testId: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-md bg-muted p-1" role="radiogroup" data-testid={testId}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          disabled={option.disabled}
          title={option.title}
          data-testid={`${testId}-${option.value}`}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded px-2 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            value === option.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Switching style keeps captured frames but converts "no background"
 *  (card) and "uncaptured frame" (frame) placeholders into each other, so a
 *  'frame' carousel always knows which slides still need a capture. */
function applyStyle(
  carousel: QuoteCarousel,
  style: QuoteSlideStyle,
  frameSource: QuoteFrameSource,
): QuoteCarousel {
  const slides = carousel.slides.map((slide): QuoteSlide => {
    if (style === 'frame' && slide.background === null) {
      return {
        ...slide,
        background: {
          kind: 'frame', url: null, source: frameSource, time: null,
        },
      };
    }
    // Only automatic placeholders revert; a hand-picked frame stays.
    if (
      style === 'card'
      && slide.background?.kind === 'frame'
      && slide.background.url === null
      && slide.background.time === null
    ) {
      return { ...slide, background: null };
    }
    return slide;
  });
  return { ...carousel, style, slides };
}

interface CarouselSettingsPanelProps {
  carousel: QuoteCarousel;
  onChange: (carousel: QuoteCarousel) => void;
  /** Full brand kits (templates), rendered as preview cards. */
  brandKits: readonly EditorTemplateKit[];
  selectedSlideId: string | null;
  /** Videos frames can be grabbed from; `edited` null = unavailable. */
  frameSources: FrameCaptureSources;
  /** Automatic capture time of a slide on a source (null = unknown axis). */
  autoFrameTime: (slide: QuoteSlide, source: QuoteFrameSource) => number | null;
  /** Brand background color, the starting value for a slide's "Cor" background. */
  defaultBackgroundColor: string;
  /** Brand palette quick picks for the color input. */
  colorPresets?: string[];
  /** The selected template's look, which the "Texto" section overrides. */
  templateBrand?: QuoteSlideBrand;
  /** State of the original (source video) URL query, for the frame picker. */
  originalStatus?: 'ready' | 'loading' | 'error';
  /** Refetches the source video query. */
  onRetryOriginal?: () => void;
}

/** The carousel with `textStyle` set, or removed (back to the template). */
function withTextStyle(carousel: QuoteCarousel, textStyle: QuoteTextStyle | undefined): QuoteCarousel {
  const next = { ...carousel };
  if (textStyle) next.textStyle = textStyle;
  else delete next.textStyle;
  return next;
}

export const CarouselSettingsPanel: React.FC<CarouselSettingsPanelProps> = ({
  carousel, onChange, brandKits, selectedSlideId, frameSources, autoFrameTime,
  defaultBackgroundColor, colorPresets, templateBrand = DEFAULT_QUOTE_BRAND,
  originalStatus = 'ready', onRetryOriginal,
}) => {
  const { t } = useTranslation();
  const resolvedText = resolveQuoteTextStyle({
    brand: templateBrand, style: carousel.style, textStyle: carousel.textStyle,
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const editedAvailable = !!frameSources.edited;
  // What new automatic frames use and what the toggle shows.
  const frameSource = resolveEffectiveFrameSource(carousel.frameSource, frameSources);
  const usesFrames = carousel.style === 'frame'
    || carousel.slides.some((slide) => slide.background?.kind === 'frame');
  const selectedIndex = carousel.slides.findIndex((slide) => slide.id === selectedSlideId);
  const selectedSlide = selectedIndex >= 0 ? carousel.slides[selectedIndex] : null;
  // A card slide with no background uses the brand color: neither option.
  const backgroundKind = selectedSlide?.background?.kind
    ?? (carousel.style === 'frame' ? 'frame' : null);
  const selectedFrame = selectedSlide?.background?.kind === 'frame' ? selectedSlide.background : null;
  const frameSourceOptions = (): SegmentedOption<QuoteFrameSource>[] => [
    { value: 'original', label: t('quoteCarousel.frameSource.original') },
    {
      value: 'edited',
      label: t('quoteCarousel.frameSource.edited'),
      disabled: !editedAvailable,
      title: editedAvailable ? undefined : t('quoteCarousel.framePicker.editedUnavailable'),
    },
  ];

  const updateSelectedSlide = (patch: Partial<QuoteSlide>) => {
    if (selectedIndex < 0) return;
    onChange({
      ...carousel,
      slides: carousel.slides.map((slide, i) => (i === selectedIndex ? { ...slide, ...patch } : slide)),
    });
  };

  return (
    <div className="flex flex-col gap-5" data-testid="quote-carousel-settings">
      <div className="space-y-2">
        <Label className="text-xs">{t('quoteCarousel.settings.style')}</Label>
        <Segmented<QuoteSlideStyle>
          testId="quote-style"
          value={carousel.style}
          onChange={(style) => onChange(applyStyle(carousel, style, frameSource))}
          options={[
            { value: 'card', label: t('quoteCarousel.settings.styleCard') },
            { value: 'frame', label: t('quoteCarousel.settings.styleFrame') },
          ]}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">{t('quoteCarousel.settings.format')}</Label>
        <Segmented<QuoteCarouselFormat>
          testId="quote-format"
          value={carousel.format}
          onChange={(format) => onChange({ ...carousel, format })}
          options={[
            { value: 'portrait_4x5', label: t('quoteCarousel.settings.format4x5') },
            { value: 'portrait_9x16', label: t('quoteCarousel.settings.format9x16') },
          ]}
        />
      </div>

      <div className="space-y-2" data-testid="quote-templates">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs">{t('quoteCarousel.settings.templates')}</Label>
          {carousel.brandKitId && (
            <button
              type="button"
              data-testid="quote-template-clear"
              className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              onClick={() => onChange({ ...carousel, brandKitId: null })}
            >
              {t('quoteCarousel.settings.noTemplate')}
            </button>
          )}
        </div>
        <EditorTemplatesCarousel
          kits={brandKits}
          selectedId={carousel.brandKitId}
          onSelect={(id) => onChange({ ...carousel, brandKitId: id })}
        />
      </div>

      <QuoteTextStyleSection
        textStyle={carousel.textStyle}
        brand={resolvedText.brand}
        typography={resolvedText.typography}
        colorPresets={colorPresets}
        onChange={(textStyle) => onChange(withTextStyle(carousel, textStyle))}
      />

      {usesFrames && (
        <div className="space-y-2">
          <Label className="text-xs">{t('quoteCarousel.settings.frameSource')}</Label>
          <Segmented<QuoteFrameSource>
            testId="quote-frame-source"
            value={frameSource}
            onChange={(source) => {
              if (source === 'edited' && !editedAvailable) return;
              onChange(applyCarouselFrameSource(carousel, source));
            }}
            options={frameSourceOptions()}
          />
          {!editedAvailable && (
            <p className="text-[11px] text-muted-foreground" data-testid="quote-frame-source-edited-unavailable">
              {t('quoteCarousel.framePicker.editedUnavailable')}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="quote-handle" className="text-xs">{t('quoteCarousel.settings.handle')}</Label>
        <Input
          id="quote-handle"
          data-testid="quote-handle"
          className="h-8 text-xs"
          value={carousel.handle}
          maxLength={MAX_HANDLE_CHARS}
          placeholder={t('quoteCarousel.settings.handlePlaceholder')}
          onChange={(event) => onChange({ ...carousel, handle: event.target.value.slice(0, MAX_HANDLE_CHARS) })}
        />
      </div>

      <div className="space-y-2 border-t pt-4">
        <Label className="text-xs">
          {selectedSlide
            ? t('quoteCarousel.settings.slideBackground', { number: selectedIndex + 1 })
            : t('quoteCarousel.settings.slideBackgroundNone')}
        </Label>
        {selectedSlide && (
          <>
            <Segmented<'frame' | 'color'>
              testId="quote-slide-background"
              value={backgroundKind}
              onChange={(kind) => {
                if (kind === backgroundKind) return;
                if (kind === 'color') {
                  updateSelectedSlide({ background: { kind: 'color', color: defaultBackgroundColor } });
                } else {
                  updateSelectedSlide({
                    background: {
                      kind: 'frame', url: null, source: frameSource, time: null,
                    },
                  });
                }
              }}
              options={[
                { value: 'frame', label: t('quoteCarousel.settings.backgroundFrame') },
                { value: 'color', label: t('quoteCarousel.settings.backgroundColor') },
              ]}
            />
            {backgroundKind === 'frame' && (
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-[11px] text-muted-foreground" data-testid="quote-slide-frame-mode">
                  {selectedFrame && selectedFrame.time !== null
                    ? t('quoteCarousel.settings.framePicked', {
                      time: formatFrameTime(selectedFrame.time),
                      source: t(`quoteCarousel.frameSource.${selectedFrame.source}`),
                    })
                    : t('quoteCarousel.settings.frameAuto')}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0 text-xs"
                  data-testid="quote-slide-pick-frame"
                  // A failed source query still opens the picker: it explains
                  // the failure and offers a retry.
                  disabled={!frameSources.original && !frameSources.edited && originalStatus !== 'error'}
                  onClick={() => setPickerOpen(true)}
                >
                  <Film className="mr-1 h-3.5 w-3.5" />
                  {t('quoteCarousel.settings.pickFrame')}
                </Button>
              </div>
            )}
            {selectedSlide.background?.kind === 'color' && (
              <ColorInput
                testId="quote-slide-background-color"
                value={selectedSlide.background.color}
                presets={colorPresets}
                onChange={(color) => updateSelectedSlide({ background: { kind: 'color', color } })}
              />
            )}
          </>
        )}
      </div>

      {selectedSlide && (
        <FramePickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          sources={frameSources}
          initialSource={selectedFrame?.source ?? frameSource}
          initialTime={selectedFrame?.time ?? null}
          autoTimeFor={(source) => autoFrameTime(selectedSlide, source)}
          originalStatus={originalStatus}
          onRetryOriginal={onRetryOriginal}
          onUse={({ source, time }) => {
            // Same pick as the one already captured: keep its still.
            if (
              selectedFrame?.source === source
              && selectedFrame.time !== null
              && Math.round(selectedFrame.time * EXPORT_FPS) === Math.round(time * EXPORT_FPS)
            ) return;
            updateSelectedSlide({
              background: {
                kind: 'frame', url: null, source, time,
              },
            });
          }}
          onResetAuto={() => {
            if (selectedFrame?.time === null && selectedFrame.source === frameSource) return;
            updateSelectedSlide({
              background: {
                kind: 'frame', url: null, source: frameSource, time: null,
              },
            });
          }}
        />
      )}
    </div>
  );
};

export default CarouselSettingsPanel;
